import '@aimsos/agentic-studio/xyflow.css';
import { useEffect, useState } from 'react';
import { useAuth } from '@aimsos/auth';
import { ShellAuthProvider } from '@aimsos/auth';
import { useTranslation } from '@aimsos/i18n';
import { AgenticStudioApp } from '@aimsos/agentic-studio';
import { registerNamespace, i18n } from '../i18n/bootstrap.js';

/**
 * Mounts Agentic Studio inside the Core Shell.
 *
 * Same embedding contract as GovernanceEmbed: token + window.__AIMS_SHELL__,
 * ShellAuthProvider, and shell + package i18n namespaces. Reads the unified
 * useAuth() (our own backend JWT) instead of useAuth0() directly, so this
 * still mounts correctly on a {slug}.{rootDomain} subdomain landed on via a
 * cross-subdomain handoff, where Auth0 SDK itself has no session.
 */
const AgenticStudioEmbed = () => {
  const { user, token, isAuthenticated, getAccessTokenSilently } = useAuth();
  const [shellI18nReady, setShellI18nReady] = useState(false);
  const [appI18nReady, setAppI18nReady] = useState(false);
  const { t } = useTranslation('agentic-studio');

  useEffect(() => {
    let cancelled = false;
    import('../i18n/registerAgenticStudio.js')
      .then(({ registerAgenticStudioI18n }) => {
        registerAgenticStudioI18n(registerNamespace);
        return i18n.loadNamespaces('agentic-studio');
      })
      .then(() => {
        if (!cancelled) setShellI18nReady(true);
      })
      .catch((err) => console.error('[AgenticStudioEmbed] shell i18n:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    import('@aimsos/agentic-studio/i18n/register')
      .then(({ registerAgenticStudioAppI18n }) => {
        registerAgenticStudioAppI18n(registerNamespace);
        return i18n.loadNamespaces('agentic-studio-app');
      })
      .then(() => {
        if (!cancelled) setAppI18nReady(true);
      })
      .catch((err) => console.error('[AgenticStudioEmbed] agentic-studio-app i18n:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    window.__AIMS_SHELL__ = {
      mounted: true,
      apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
      // V0 gateways (CT-5907): one per root (network, worker, playbook) plus agent-api for graph. Agentic
      // Studio talks to V0 only (no V1 fallback): a root whose base URL is missing fails with a
      // configuration error.
      // network root serves network, ai-wizard, builder, knowledge-config, marketplace, workspace-layout.
      apiBaseUrlNetwork: import.meta.env.VITE_API_BASE_URL_NETWORK,
      // worker root serves worker and squad.
      apiBaseUrlWorker: import.meta.env.VITE_API_BASE_URL_WORKER,
      apiBaseUrlPlaybook: import.meta.env.VITE_API_BASE_URL_PLAYBOOK,
      // agent-api serves the graph runs (/v0/graph/...) through the custom domain with the
      // /agent path (e.g. https://api.dev.aimsos.app/agent). Not workspace-api (…_WS).
      apiBaseUrlAgent: import.meta.env.VITE_API_BASE_URL_AGENT,
    };
    return () => {
      delete window.__AIMS_SHELL__;
    };
  }, [isAuthenticated]);

  if (!isAuthenticated || !token) {
    if (!shellI18nReady) return null;
    return (
      <div className="flex flex-1 items-center justify-center p-6 text-sm text-content-muted">
        {t('embed.loadingToken')}
      </div>
    );
  }

  if (!appI18nReady) return null;

  return (
    <ShellAuthProvider
      token={token}
      user={user}
      isAuthenticated={isAuthenticated}
      getAccessTokenSilently={getAccessTokenSilently}
    >
      <AgenticStudioApp />
    </ShellAuthProvider>
  );
};

export default AgenticStudioEmbed;
